import { useState } from 'react';
import { getAllGalleryImages, getImagesByCategory, getGalleryCategories } from '../team-data/gallery';
import SEO from '../components/SEO';
import ScrollToTop from '../components/ScrollToTop';

function GalleryPage() {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedImage, setSelectedImage] = useState(null);
    
    const categories = getGalleryCategories();
    const allImages = getAllGalleryImages();
    
    const displayImages = selectedCategory === 'all' 
        ? allImages 
        : getImagesByCategory(selectedCategory);

    const openModal = (image) => {
        setSelectedImage(image);
    };

    const closeModal = () => {
        setSelectedImage(null);
    };

    return (
        <>
            <SEO 
                title="WeFlare Gallery"
                description="Explore our journey of empowering African youth through various programs and initiatives in pictures" 
                keywords="WeFlare gallery, youth empowerment photos, African youth programs, SDG initiatives, community impact"
                url="https://weflare.org/gallery"
            />
            <ScrollToTop />
            <div className="bg-pattern-light py-3">
                <div className="container page-length">
                    <div className="row justify-content-center">
                        <div className="col-12">
                            <h1 className="text1 text-center mb-5">Our Gallery</h1>
                        </div>
                        
                        <div className="col-lg-10">
                            {/* Section Header */}
                            <div className="text-center mb-5">
                                <h2 className="text-display mb-4" style={{fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', color: 'var(--gray-800)'}}>
                                    Our Impact in Pictures
                                </h2>
                                <p className="lead text2 text-muted mb-5" style={{fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto'}}>
                                    Explore our journey of empowering African youth through various programs and initiatives.
                                </p>
                            </div>

                            {/* Category Filter */}
                            <div className="text-center mb-5">
                                <div className="btn-group flex-wrap" role="group">
                                    <button
                                        type="button"
                                        className={`btn ${selectedCategory === 'all' ? 'btn-primary' : 'btn-outline-primary'} me-2 mb-2`}
                                        onClick={() => setSelectedCategory('all')}
                                    >
                                        All ({allImages.length})
                                    </button>
                                    {categories.map((category) => (
                                        <button
                                            key={category.id}
                                            type="button"
                                            className={`btn ${selectedCategory === category.id ? 'btn-primary' : 'btn-outline-primary'} me-2 mb-2`}
                                            onClick={() => setSelectedCategory(category.id)}
                                        >
                                            {category.name} ({category.count})
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Gallery Grid */}
                            <div className="row g-4">
                                {displayImages.map((image, index) => (
                                    <div key={image.id} className="col-sm-6 col-md-4 col-lg-3">
                                        <div 
                                            className="gallery-item animate-fade-in-up cursor-pointer"
                                            style={{animationDelay: `${index * 0.1}s`}}
                                            onClick={() => openModal(image)}
                                        >
                                            <div className="position-relative overflow-hidden rounded-3">
                                                <img 
                                                    src={image.image} 
                                                    alt={image.alt}
                                                    className="img-fluid w-100"
                                                    style={{
                                                        height: '250px',
                                                        objectFit: 'cover',
                                                        transition: 'transform 0.3s ease'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                                                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                                />
                                                <div className="gallery-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-end p-3"
                                                     style={{
                                                         background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                                                         opacity: 0,
                                                         transition: 'opacity 0.3s ease'
                                                     }}
                                                     onMouseEnter={(e) => e.target.style.opacity = 1}
                                                     onMouseLeave={(e) => e.target.style.opacity = 0}
                                                >
                                                    <div className="text-white">
                                                        <h6 className="mb-1">{image.title}</h6>
                                                        <small className="text-light">{image.category}</small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* No images message */}
                            {displayImages.length === 0 && (
                                <div className="text-center py-5">
                                    <p className="text-muted">No images found in this category.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Modal for image preview */}
                {selectedImage && (
                    <div 
                        className="modal fade show d-block" 
                        style={{backgroundColor: 'rgba(0,0,0,0.8)'}}
                        onClick={closeModal}
                    >
                        <div className="modal-dialog modal-lg modal-dialog-centered">
                            <div className="modal-content bg-transparent border-0">
                                <div className="modal-body p-0 text-center">
                                    <button 
                                        type="button" 
                                        className="btn-close btn-close-white position-absolute top-0 end-0 m-3"
                                        style={{zIndex: 1050}}
                                        onClick={closeModal}
                                    ></button>
                                    <img 
                                        src={selectedImage.image} 
                                        alt={selectedImage.alt}
                                        className="img-fluid rounded-3"
                                        style={{maxHeight: '80vh'}}
                                    />
                                    <div className="bg-white rounded-3 p-3 mt-3">
                                        <h5 className="mb-2">{selectedImage.title}</h5>
                                        <p className="text-muted mb-1">{selectedImage.description}</p>
                                        <small className="badge bg-primary">{selectedImage.category}</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default GalleryPage;